import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import Auth from "./auth";

const SENTIMENT_OPTIONS = [
  { value: "very_happy", label: "Very Happy", emoji: "😄", colorClass: "bg-success" },
  { value: "happy", label: "Happy", emoji: "🙂", colorClass: "bg-success" },
  { value: "neutral", label: "Neutral", emoji: "😐", colorClass: "bg-warning" },
  { value: "unhappy", label: "Unhappy", emoji: "🙁", colorClass: "bg-warning" },
  { value: "very_unhappy", label: "Very Unhappy", emoji: "😠", colorClass: "bg-danger" },
];

function authHeaders() {
  const token = Auth.getToken();
  return {
    "Content-Type": "application/json",
    authorization: token ? `Bearer ${token}` : "",
  };
}

export default function FeedbackModal({ authUser, onClose }) {
  const [sentiment, setSentiment] = useState(null);
  const [feedbackText, setFeedbackText] = useState("");
  const [submitState, setSubmitState] = useState("idle");
  const [errorMessage, setErrorMessage] = useState(null);

  const canSubmit = useMemo(() => sentiment !== null && submitState !== "submitting", [sentiment, submitState]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!sentiment) {
      setErrorMessage("Please select a rating.");
      return;
    }

    setSubmitState("submitting");
    setErrorMessage(null);

    const payload = {
      sentiment,
      feedback_text: feedbackText.trim() || undefined,
      page_context: window.location.pathname,
    };

    try {
      const response = await fetch("/api/feedback/submit", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Unable to submit feedback right now.");
      }

      await response.json();
      setSubmitState("success");
      setFeedbackText("");
      setSentiment(null);
    } catch (error) {
      setSubmitState("error");
      setErrorMessage(error?.message || "Unable to submit feedback right now.");
    }
  };

  return (
    <div className="position-fixed top-0 start-0 end-0 bottom-0 d-flex align-items-center justify-content-center p-3" style={{ zIndex: 1080 }} role="dialog" aria-modal="true" aria-labelledby="feedback-modal-title">
      <div className="position-absolute top-0 start-0 end-0 bottom-0 bg-dark" style={{ opacity: 0.45 }} onClick={onClose} />

      <div className="position-relative bg-white rounded-4 shadow border w-100" style={{ maxWidth: 900 }}>
        <div className="d-flex align-items-center justify-content-between border-bottom px-4 py-3">
          <h2 id="feedback-modal-title" className="h3 mb-0">How are we doing?</h2>
          <button type="button" onClick={onClose} className="btn btn-sm btn-outline-secondary" aria-label="Close feedback dialog">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-4 py-4">
          <fieldset>
            <legend className="visually-hidden">Select a satisfaction rating</legend>
            <div className="d-flex flex-wrap align-items-center gap-3">
              {SENTIMENT_OPTIONS.map((option) => {
                const selected = sentiment === option.value;
                return (
                  <label key={option.value} className="d-flex align-items-center gap-2" style={{ cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="feedback-sentiment"
                      value={option.value}
                      checked={selected}
                      onChange={() => setSentiment(option.value)}
                    />
                    <span
                      className={`rounded-circle text-white d-inline-flex align-items-center justify-content-center ${option.colorClass}`}
                      style={{ width: 44, height: 44, transform: selected ? "scale(1.06)" : "scale(1)" }}
                      aria-hidden="true"
                    >
                      {option.emoji}
                    </span>
                    <span className="visually-hidden">{option.label}</span>
                  </label>
                );
              })}
            </div>
          </fieldset>

          <div className="mt-4">
            <label htmlFor="feedback-text" className="form-label fw-semibold">
              Enter any additional feedback <span className="text-body-secondary">(optional)</span>
            </label>
            <textarea
              id="feedback-text"
              value={feedbackText}
              onChange={(event) => setFeedbackText(event.target.value)}
              rows={4}
              maxLength={2000}
              className="form-control"
              placeholder="Anything that worked well, felt confusing, or could be improved"
            />
          </div>

          <p className="mt-3 small text-body-secondary mb-0">
            Signed in as {authUser?.username || authUser?.name || "Unknown"} ({authUser?.email || "n/a"})
          </p>

          {errorMessage ? <div className="alert alert-danger mt-3 mb-0">{errorMessage}</div> : null}
          {submitState === "success" ? <div className="alert alert-success mt-3 mb-0">Thanks for your feedback. It was submitted successfully.</div> : null}

          <div className="mt-4 d-flex align-items-center justify-content-end gap-2">
            <button type="button" onClick={onClose} className="btn btn-outline-secondary">Close</button>
            <button type="submit" disabled={!canSubmit} className="btn btn-primary d-inline-flex align-items-center gap-2">
              {submitState === "submitting" ? <span className="spinner-border spinner-border-sm" aria-hidden="true" /> : null}
              Send feedback
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
